import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import './Calls.scss';

import CallKeyMomentFilters from './CallKeyMomentFilters';
import CallsFilters from './CallsFilters';
import CallPreviewNew from './CallPreviewNew';
import CallPreview from './CallPreview';
import CallPreviewPanel from './CallPreviewPanel';
import Loader from '../General/Loader';
import CallToDealMatchingModal from '../Modals/CallToDealMatchingModal';

import { hasZoomConnected } from '../../helpers/user_helpers';

const TESTING_DEAL_MATCHING_MODAL = false; // MAKE FALSE BEFORE LANDING
const TEST_PREVIEW_CALL = false; // MAKE FALSE BEFORE LANDING

const CALL_LIMIT = 10;

class Calls extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    calls: PropTypes.object.isRequired,
    deals: PropTypes.object.isRequired,
    getAllDeals: PropTypes.func.isRequired,
    updateChatParticipant: PropTypes.func.isRequired,
    getKeyMomentTypes: PropTypes.func.isRequired,
  };

  state = {
    // Filtering
    selectedHealthScore: null,
    filtersVisible: true,
    expandAllCards: true,
    showOnlyUnmatched: false,

    // Preview Side Panel
    previewingCall: null,

    // UI Handling
    showingNewCallCells: true, // Keep this in case we need to revert
    showingOldCallCellsCallIds: [],
    isFirstFetch: true,
    isFetchingCalls: false,
    isFetchingNewPage: false,
    selectedCallForMatchingToDeal: null,

    // Query Params
    searchVal: '',
    selectedKeyMomentTypes: [],
    page: 0,
  };

  componentDidMount() {
    this.searchDebouncer = AwesomeDebouncePromise(this.syncCalls, 500);
    this.syncCalls();
    this.props.getAllDeals();
    this.infiniteScrollHandler();
    window.__ADD_EVENT__('Calls - Page View');
  }

  infiniteScrollHandler = () => {
    this.callsContainerRef &&
      this.callsContainerRef.addEventListener('scroll', (e) => {
        const pixelsFromBottom = e.target.scrollHeight - e.target.offsetHeight - e.target.scrollTop;
        if (pixelsFromBottom < 250 && !this.state.isFetchingNewPage && !this.state.receivedAllResults) {
          this.setState({ isFetchingNewPage: true, page: this.state.page + 1 }, this.syncCalls);
        }
      });
  };

  syncCalls = () => {
    const { getAllCalls } = this.props;
    const { searchVal, selectedHealthScore, selectedKeyMomentTypes, selectedStartDate, selectedEndDate, page } = this.state;
    let params = {
      ...(searchVal ? { search: searchVal } : {}),
      ...(selectedHealthScore ? { health: selectedHealthScore } : {}),
      ...(selectedKeyMomentTypes.length ? { moment_type_id: selectedKeyMomentTypes[0].id } : {}),
      ...(selectedStartDate ? { start_date: selectedStartDate } : {}),
      ...(selectedEndDate ? { end_date: selectedEndDate } : {}),
      offset: page * CALL_LIMIT,
      limit: CALL_LIMIT,
    };

    this.setState({ isFetchingCalls: true });
    getAllCalls(params)
      .then((resp) => {
        this.setState({
          receivedAllResults: resp?.calls?.length !== CALL_LIMIT,
          selectedCallForMatchingToDeal: TESTING_DEAL_MATCHING_MODAL ? _.first(resp.calls) : this.state.selectedCallForMatchingToDeal,
          previewingCall: TEST_PREVIEW_CALL ? _.first(resp.calls.filter((c) => !_.isEmpty(c.summary))) : this.state.previewingCall,
          callPreviewVisible: TEST_PREVIEW_CALL ? true : this.state.callPreviewVisible,
        });
      }, console.error)
      .finally(() => {
        this.setState({ isFetchingCalls: false, isFetchingNewPage: false, isFirstFetch: false });
      });
  };

  setSelectedHealthScore = (newScore) => {
    this.setState({ selectedHealthScore: newScore, page: 0 }, () => {
      this.syncCalls();
      this.scrollToTop();
    });
  };

  scrollToTop = () => this.callsContainerRef && this.callsContainerRef.scrollTo(0, 0);

  toggleShowOnlyUnmatchedCards = () => this.setState({ showOnlyUnmatched: !this.state.showOnlyUnmatched });

  toggleKeyMomentTypeFilter = (type) => {
    const { selectedKeyMomentTypes } = this.state;
    const isSelected = _.find(selectedKeyMomentTypes, (t) => t.id === type.id);
    this.setState(
      // Allow Multiple => { selectedKeyMomentTypes: isSelected ? selectedKeyMomentTypes.filter((t) => t.id !== type.id) : [...selectedKeyMomentTypes, type], page: 0 },
      { selectedKeyMomentTypes: isSelected ? [] : [type] }, // Allow One
      () => {
        this.syncCalls();
        this.scrollToTop();
      }
    );
  };

  render() {
    const { calls, deals, user } = this.props;
    const { allCalls } = calls;
    const {
      selectedHealthScore,
      searchVal,
      expandAllCards,
      showOnlyUnmatched,
      previewingCall,
      callPreviewVisible,
      filtersVisible,
      isFetchingCalls,
      isFirstFetch,
      isFetchingNewPage,
      selectedKeyMomentTypes,
      showingNewCallCells,
      selectedCallForMatchingToDeal,
    } = this.state;

    const sortedCalls = _.orderBy(allCalls, 'created_at', 'desc');
    const finalCalls = _.filter(sortedCalls, (call) => {
      if (showOnlyUnmatched && call.deal) return false;
      return true;
    });

    const changeSearch = (newVal) => {
      this.setState({ searchVal: newVal, isFetchingNewPage: true, page: 0 });
      this.searchDebouncer();
    };

    const toggleFilters = () => {
      this.setState({ filtersVisible: !filtersVisible, callPreviewVisible: false, previewingCall: null });
      window.__ADD_EVENT__('Calls - Toggle Filters');
    };

    const selectCallForPreview = (call) => {
      if (call) {
        window.__ADD_EVENT__('Calls - Select Call Preview', { call });
        this.setState({ filtersVisible: false, callPreviewVisible: true, previewingCall: call });
      } else {
        this.setState({ filtersVisible: false, callPreviewVisible: !callPreviewVisible });
        // Add delay for visual cleanliness
        setTimeout(() => this.setState({ previewingCall: null }), 400);
      }
    };

    const isBaseState = !calls.length && !searchVal;
    return (
      <div
        className={cn('calls-outer-container', {
          new: showingNewCallCells,
          'filters-visible': filtersVisible,
          'call-preview-panel-visible': callPreviewVisible,
        })}
      >
        <CallToDealMatchingModal
          deals={deals}
          call={selectedCallForMatchingToDeal}
          visible={!!selectedCallForMatchingToDeal}
          close={() => this.setState({ selectedCallForMatchingToDeal: null })}
        />
        <div className='calls-filters-container'>
          <CallsFilters
            searchVal={searchVal}
            changeSearch={changeSearch}
            filtersVisible={filtersVisible}
            toggleFilters={toggleFilters}
            selectedHealthScore={selectedHealthScore}
            expandAllCards={expandAllCards}
            showOnlyUnmatched={showOnlyUnmatched}
            toggleShowOnlyUnmatchedCards={this.toggleShowOnlyUnmatchedCards}
            setSelectedHealthScore={this.setSelectedHealthScore}
          />
        </div>
        <div ref={(ref) => (this.callsContainerRef = ref)} className={cn('calls-main-container')}>
          <div className={cn('calls-main-inner-container')}>
            {showingNewCallCells && this.__NEW_CALL_CELLS__ && (
              <CallKeyMomentFilters
                selectedKeyMomentTypes={selectedKeyMomentTypes}
                toggleKeyMomentTypeFilter={this.toggleKeyMomentTypeFilter}
                calls={calls}
                getKeyMomentTypes={this.props.getKeyMomentTypes}
              />
            )}
            <div
              className={cn('call-results', {
                searching: isFetchingCalls && !isFirstFetch && !isFetchingNewPage,
              })}
            >
              {finalCalls.map((call, idx) =>
                this.__NEW_CALL_CELLS__ ? (
                  <CallPreviewNew
                    deleteCall={this.props.deleteCall}
                    previewCall={() => selectCallForPreview(call)}
                    expandAllCards={expandAllCards}
                    updateChatParticipant={this.props.updateChatParticipant}
                    selectForMatchingToDeal={() => this.setState({ selectedCallForMatchingToDeal: call })}
                    searchVal={searchVal}
                    syncCalls={this.syncCalls}
                    key={`${call.id}-${idx}`}
                    call={call}
                  />
                ) : (
                  <CallPreview
                    deleteCall={this.props.deleteCall}
                    previewCall={() => selectCallForPreview(call)}
                    expandAllCards={expandAllCards}
                    updateChatParticipant={this.props.updateChatParticipant}
                    selectForMatchingToDeal={() => this.setState({ selectedCallForMatchingToDeal: call })}
                    searchVal={searchVal}
                    syncCalls={this.syncCalls}
                    key={`${call.id}-${idx}`}
                    call={call}
                  />
                )
              )}
            </div>
            {!isBaseState || !!finalCalls.length ? null : selectedHealthScore ? (
              <div className='empty-calls-view'>No calls match the current filters.</div>
            ) : !hasZoomConnected(user) ? (
              <div className='empty-calls-view'>
                <div>You first must connect your zoom account on your{` `}</div>
                <Link to='/account'>account page</Link>
              </div>
            ) : (
              <div className='empty-calls-view'>Your zoom calls will appear here.</div>
            )}
          </div>
          {isFetchingNewPage && (
            <div className='loading-new-page-container'>
              <Loader size={75} />
            </div>
          )}
        </div>
        <div className='call-preview-panel-container'>
          <CallPreviewPanel closePanel={() => selectCallForPreview()} call={previewingCall} />
        </div>
      </div>
    );
  }
}

export default Calls;
